import React from "react";
import { FaLinkedin, FaInstagram, FaYoutube } from "react-icons/fa";
import { PiXLogoBold } from "react-icons/pi";
import PolicyModal from "../Policy/PolicyModal";
import CrisisBanner from "./CrisisBanner";
import Logo from "../../assets/image/header/mindtherapiyLogo.svg";
import { useTranslation } from "react-i18next";
import { LANDING_PAGES } from "../../pages/LandingPages/landingPageConfigs";

const Footer = () => {
  const { t } = useTranslation();

  const [openPrivacy, setOpenPrivacy] = React.useState(false);
  const [openKVKKInfo, setOpenKVKKInfo] = React.useState(false);
  const [openUserAgreement, setOpenUserAgreement] = React.useState(false);
  const [openTermsAndConditions, setOpenTermsAndConditions] = React.useState(false);
  const [openCookiePolicy, setOpenCookiePolicy] = React.useState(false);

  const termsContent = t("termsAndConditionsContent", { returnObjects: true });
  const privacyContent = t("privacyPolicyContent", { returnObjects: true });
  const kvkkContent = t("GDPRInformationTextContent", { returnObjects: true });
  const userAgreementContent = t("userAgreementContent", { returnObjects: true });
  const cookiePolicyContent = t("cookiePolicyContent", { returnObjects: true });

  const renderPolicyBlocks = (blocks) => {
    if (!Array.isArray(blocks)) {
      return <p className="text-gray-700">{blocks}</p>;
    }

    return blocks.map((block, index) => {
      if (block.type === "heading") {
        const HeadingTag = block.level === 4 ? "h4" : block.level === 3 ? "h3" : "h2";
        const headingBase = "font-semibold text-primary";
        const headingSizing =
          block.level === 2
            ? "text-2xl"
            : block.level === 3
            ? "text-xl"
            : "text-lg";
        const isPrimaryHeading = index === 0;
        const headingClasses = [
          isPrimaryHeading ? "mt-0" : "mt-6",
          headingBase,
          isPrimaryHeading ? "text-3xl text-center" : headingSizing
        ].join(" ");
        return (
          <HeadingTag
            key={`heading-${index}`}
            className={headingClasses}
          >
            {block.content}
          </HeadingTag>
        );
      }

      if (block.type === "paragraph") {
        const paragraphClasses = [
          "mt-2",
          "text-gray-700",
          block.emphasis ? "font-semibold text-primary" : ""
        ]
          .filter(Boolean)
          .join(" ");
        return (
          <p key={`paragraph-${index}`} className={paragraphClasses}>
            {block.content}
          </p>
        );
      }

      if (block.type === "list") {
        const listClasses = [
          "mt-2",
          "list-disc",
          "space-y-1",
          "pl-6",
          "text-gray-700",
          "marker:text-primary",
          block.emphasis ? "font-semibold text-primary" : ""
        ]
          .filter(Boolean)
          .join(" ");
        return (
          <ul
            key={`list-${index}`}
            className={listClasses}
          >
            {block.items?.map((item, itemIndex) => (
              <li key={`list-${index}-${itemIndex}`}>{item}</li>
            ))}
          </ul>
        );
      }

      return null;
    });
  };

  return (
    <>
      {/* Crisis Banner - Footer'ın hemen üstünde */}
      <CrisisBanner />

      {/* Ana Footer */}
      <footer className="notranslate bg-tertiary text-primary py-8 px-4" translate="no">
        <div className="flex flex-col lg:flex-row justify-between items-center lg:items-start text-center lg:text-left">
          {/* Sol Kısım */}
          <div className="flex flex-col items-center lg:items-start mb-8 lg:mb-0">
            {/* Logo */}
            <div className="w-[220px] md:w-[260px] ml-5 -mb-5">
              <img
                src={Logo}
                alt="Therhappy Logo"
                className="w-full h-full mx-auto lg:mx-0"
              />
            </div>
            {/* Sosyal Medya Linkleri */}
            <div className="flex space-x-8 mt-6 lg:ml-12">
              <a
                href="https://www.instagram.com/mindtherapycounselling/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaInstagram className="w-6 h-6 text-[#E1306C] hover:text-primary" />
              </a>
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                <PiXLogoBold className="w-6 h-6 text-black hover:text-primary" />
              </a>
              <a
                href="https://www.linkedin.com/company/mind-therapy-counselling/posts/?feedView=all"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaLinkedin className="w-6 h-6 text-[#0077B5] hover:text-primary" />
              </a>
              <a
                href={`https://www.youtube.com/channel/${process.env.REACT_APP_YOUTUBE_CHANNEL_ID}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaYoutube className="w-6 h-6 text-[#FF0000] hover:text-primary" />
              </a>
            </div>
            {/* Telif Hakkı */}
            <p className="text-sm text-primary font-roboto mt-16">
              {t("copyright")}
            </p>
          </div>

          {/* Sağ Kısım */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 lg:gap-12 mt-8 lg:mt-0">
            {/* Our Services */}
            <div>
              <h3 className="font-roboto text-[24px] mb-4">Our Services</h3>
              <ul className="space-y-2 text-sm">
                {LANDING_PAGES.map((page) => (
                  <li key={page.slug}>
                    <a href={`/therapy/${page.slug}`} className="hover:underline">
                      {page.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* About Us */}
            <div>
              <h3 className="font-roboto text-[24px] mb-4">
                {t("MindTherapyCounselling")}
              </h3>
              <ul className="space-y-2 text-sm">
                {/* <li><a href="#our-contributions" className="hover:underline">{t("ourContributions")}</a></li> */}
                <li>
                  <a href="/aboutUs" className="hover:underline">
                    {t("aboutUs")}
                  </a>
                </li>
                <li>
                  <a
                    href="/how-does-it-work"
                    className="hover:underline"
                    onClick={(e) => {
                      if (
                        window.location.pathname === "/how-does-it-work" ||
                        window.location.pathname === "/"
                      ) {
                        e.preventDefault();
                        const el = document.getElementById("how-does-it-work");
                        if (el) {
                          el.scrollIntoView({ behavior: "smooth", block: "start" });
                        }
                      }
                    }}
                  >
                    {t("howDoesItWork")}
                  </a>
                </li>
                <li>
                  <a href="/blog" className="hover:underline">
                    {t("blog")}
                  </a>
                </li>
                <li>
                  <a href="/faq" className="hover:underline">
                    {t("faq")}
                  </a>
                </li>
              </ul>
            </div>

            {/* Communication */}
            <div>
              <h3 className="font-roboto text-[24px] mb-4">
                {t("communication")}
              </h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <a
                    href="mailto:info@mindtherapycounselling.com"
                    className="text-primary hover:text-cards-border hover:underline cursor-pointer transition-colors duration-200"
                  >
                    info@mindtherapycounselling.com
                  </a>
                </li>
                <li>
                  <address className="not-italic">
                    <p>Suite 57-58</p>
                    <p>Capital Business Park</p>
                    <p>22 Carlton Road</p>
                    <p>South Croydon</p>
                    <p>CR2 0BS</p>
                    <p>United Kingdom</p>
                  </address>
                </li>
              </ul>
            </div>

            {/* Contracts */}
            <div>
              <h3 className="font-roboto text-[24px] mb-4">{t("contracts")}</h3>
              <ul className="space-y-2 text-sm">
              <li>
                  <button
                    onClick={() => setOpenTermsAndConditions(true)}
                    className="hover:underline"
                  >
                    {t("termsAndConditions")}
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => setOpenPrivacy(true)}
                    className="hover:underline"
                  >
                    {t("privacyPolicy")}
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => setOpenCookiePolicy(true)}
                    className="hover:underline"
                  >
                    {t("cookiePolicy")}
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => setOpenKVKKInfo(true)}
                    className="hover:underline"
                  >
                    {t("GDPRInformationText")}
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => setOpenUserAgreement(true)}
                    className="hover:underline"
                  >
                    {t("userAgreement")}
                  </button>
                </li>
              </ul>

              <PolicyModal
                isOpen={openTermsAndConditions}
                onClose={() => setOpenTermsAndConditions(false)}
                title={t("termsAndConditions")}
              >
                <div className="space-y-4 text-sm leading-6 text-gray-700">
                  {renderPolicyBlocks(termsContent)}
                </div>
              </PolicyModal>
              <PolicyModal
                isOpen={openPrivacy}
                onClose={() => setOpenPrivacy(false)}
                title={t("privacyPolicy")}
              >
                <div className="space-y-4 text-sm leading-6 text-gray-700">
                  {renderPolicyBlocks(privacyContent)}
                </div>
              </PolicyModal>
              <PolicyModal
                isOpen={openCookiePolicy}
                onClose={() => setOpenCookiePolicy(false)}
                title={t("cookiePolicy")}
              >
                <div className="space-y-4 text-sm leading-6 text-gray-700">
                  {renderPolicyBlocks(cookiePolicyContent)}
                </div>
              </PolicyModal>
              <PolicyModal
                isOpen={openKVKKInfo}
                onClose={() => setOpenKVKKInfo(false)}
                title={t("GDPRInformationText")}
              >
                <div className="space-y-4 text-sm leading-6 text-gray-700">
                  {renderPolicyBlocks(kvkkContent)}
                </div>
              </PolicyModal>
              <PolicyModal
                isOpen={openUserAgreement}
                onClose={() => setOpenUserAgreement(false)}
                title={t("userAgreement")}
              >
                <div className="space-y-4 text-sm leading-6 text-gray-700">
                  {renderPolicyBlocks(userAgreementContent)}
                </div>
              </PolicyModal>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;
